/**
 * `hierarchyId.ts` — Campfire `PartData.HierachyID` derivation for an
 * imported model tree. Pure + unit-testable (no Three.js side effects).
 *
 * ## Why the "virtual root" scheme
 *
 * On the READ/render side, `sceneEntityRenderer.placeEntityAsync` reconstructs
 * an entity as:
 *
 *     basisRoot
 *       └─ walkRoot            (synthetic wrapper, `importerVersion >= 1.0.1`)
 *            ├─ Origin         (inserted empty node at child 0)
 *            └─ "Root"         (= clone(gltf.scene), the model content)
 *                 └─ …model subtree…
 *
 * and decodes a persisted `HierachyID` via `getPartByHierarchyID(walkRoot, id,
 * shift=true)`, where the `+1` shift on the first index skips the inserted
 * `Origin`. Tracing that decode:
 *
 *     "0,"        -> walkRoot                       (the entity root)
 *     "0,0,"      -> walkRoot.children[1] = "Root"  (= clone(gltf.scene))
 *     "0,0,i,"    -> Root.children[i]     = gltf.scene.children[i]
 *     "0,0,i,j,"  -> gltf.scene.children[i].children[j]
 *
 * So `gltf.scene` itself lives at `"0,0,"` (NOT `"0,"`), with an implicit
 * *virtual root* one level above it carried by the separate `IsRoot` snapshot
 * at {@link ROOT_HIERARCHY_ID}. Authoring `gltf.scene` at `"0,"` (the obvious
 * choice) is off by one level and every part binds to the wrong node.
 *
 * ⚠ `gltf.scene` here means the UNITY content root, which is what the parse
 * step leaves in that slot (`runtime/assets/gltfUnityLayout.ts`): the loader's
 * scene wrapper for a multi-root or animated file, and the single root node
 * itself otherwise — Unity's `TryToRemoveEmptyLayers` strips the wrapper in
 * that case, so ids authored against it would read one level too deep on
 * desktop. The importer never sees the raw loader tree.
 *
 * This mirrors Unity's own upload → load round-trip: `CreateHierarchyID`
 * (`ProjectModel_Utils.cs:127`) walks up from a part to the tagged root
 * prepending sibling indices, and `WrapAndShiftOrigin` re-wraps on load.
 */

import type { Object3D } from "three";

/** HierarchyID of the entity's root snapshot (the synthetic wrapper). */
export const ROOT_HIERARCHY_ID = "0,";

/**
 * Depth-first flatten of a node's subtree — the node itself first, then each
 * child recursively in child order. Matches Unity's
 * `GetComponentsInChildren<Transform>(true)` ordering used by the upload
 * pipeline so part / snapshot indices line up 1:1.
 */
export function flattenDepthFirst(root: Object3D): Object3D[] {
  const out: Object3D[] = [];
  const walk = (node: Object3D): void => {
    out.push(node);
    for (const child of node.children) walk(child);
  };
  walk(root);
  return out;
}

/**
 * Derive the `HierachyID` for `node` within the model tree rooted at
 * `sceneRoot` (= `gltf.scene`). `node` must be `sceneRoot` or a descendant.
 *
 *   sceneRoot                       -> "0,0,"
 *   sceneRoot.children[i]           -> "0,0,i,"
 *   sceneRoot.children[i].children[j] -> "0,0,i,j,"
 */
export function deriveHierarchyId(node: Object3D, sceneRoot: Object3D): string {
  const path: number[] = [];
  let cur: Object3D | null = node;
  while (cur && cur !== sceneRoot && cur.parent) {
    path.unshift(cur.parent.children.indexOf(cur));
    cur = cur.parent;
  }
  return path.length > 0 ? `0,0,${path.join(",")},` : "0,0,";
}
